import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";

export default function Footer() {
  const { t, lang, setLang } = useLang();
  const { openAuthModal } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();

  return (
    <footer className={`border-t transition-colors duration-300 ${isDark ? "bg-[#080810] border-white/5" : "bg-gray-50 border-gray-200"}`}>
      {/* Top section */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-earth-line text-xl text-amber-400"></i>
              </div>
              <span className={`font-black text-lg font-['Space_Grotesk',sans-serif] ${isDark ? "text-white" : "text-gray-900"}`}>
                Buy the World
              </span>
            </div>
            <p className={`text-xs leading-relaxed ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {lang === "ja"
                ? "世界中の土地を所有する、新感覚の土地売買シミュレーター"
                : "A new kind of land ownership simulator for the whole world"}
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className={`text-xs font-semibold uppercase tracking-widest mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
              {lang === "ja" ? "サービス" : "Services"}
            </h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => openAuthModal("register")}
                  className={`text-sm transition-colors cursor-pointer hover:text-amber-400 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {t("footerRegister")}
                </button>
              </li>
              <li>
                <Link to="/map" className={`text-sm transition-colors hover:text-amber-400 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {t("mapTitle")}
                </Link>
              </li>
              <li>
                <a href="#auctions" className={`text-sm transition-colors hover:text-amber-400 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {t("activeAuctions")}
                </a>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className={`text-xs font-semibold uppercase tracking-widest mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
              {lang === "ja" ? "サポート" : "Support"}
            </h4>
            <ul className="space-y-2">
              <li>
                <a href="#terms" className={`text-sm transition-colors hover:text-amber-400 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {t("footerTerms")}
                </a>
              </li>
              <li>
                <a href="#privacy" className={`text-sm transition-colors hover:text-amber-400 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {t("footerPrivacy")}
                </a>
              </li>
              <li>
                <a href="#faq" className={`text-sm transition-colors hover:text-amber-400 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {t("footerFaq")}
                </a>
              </li>
            </ul>
          </div>

          {/* Payment & Language & Theme */}
          <div>
            <h4 className={`text-xs font-semibold uppercase tracking-widest mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
              {t("footerPayment")} / {t("language")}
            </h4>
            <ul className="space-y-2 mb-5">
              <li>
                <a href="#payment" className={`text-sm transition-colors hover:text-amber-400 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {lang === "ja" ? "クレジットカード" : "Credit Card"}
                </a>
              </li>
              <li>
                <a href="#payment" className={`text-sm transition-colors hover:text-amber-400 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {lang === "ja" ? "銀行振込" : "Bank Transfer"}
                </a>
              </li>
            </ul>

            {/* Language Toggle */}
            <div className={`flex items-center gap-1 rounded-full p-1 w-fit mb-3 ${isDark ? "bg-white/5" : "bg-gray-200"}`}>
              <button onClick={() => setLang("ja")}
                className={`px-4 py-1 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${lang === "ja" ? "bg-amber-400 text-black" : isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}>
                日本語
              </button>
              <button onClick={() => setLang("en")}
                className={`px-4 py-1 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${lang === "en" ? "bg-amber-400 text-black" : isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}>
                English
              </button>
            </div>

            {/* Theme Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                  isDark
                    ? "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={isDark ? "ri-sun-line text-amber-400 text-sm" : "ri-moon-line text-gray-600 text-sm"}></i>
                </div>
                <span>{isDark ? (lang === "ja" ? "ライトモード" : "Light Mode") : (lang === "ja" ? "ダークモード" : "Dark Mode")}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className={`border-t ${isDark ? "border-white/5" : "border-gray-200"}`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {[
              { icon: "ri-bank-card-line", label: "VISA" },
              { icon: "ri-bank-card-2-line", label: "Mastercard" },
              { icon: "ri-secure-payment-line", label: "PayPay" },
            ].map((p) => (
              <div key={p.label} className={`rounded px-3 py-1.5 flex items-center gap-1 ${isDark ? "bg-white/10" : "bg-gray-200"}`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${p.icon} text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}></i>
                </div>
                <span className={`text-xs whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-600"}`}>{p.label}</span>
              </div>
            ))}
          </div>
          <p className={`text-xs whitespace-nowrap ${isDark ? "text-gray-600" : "text-gray-400"}`}>{t("footerCopyright")}</p>
        </div>
      </div>
    </footer>
  );
}
